/**
 * Приём эфира: Opus из сокета — в граф Web Audio.
 *
 * ЧТО ЗДЕСЬ ТРУДНОГО. Кадры идут по TCP, а звук играется по часам
 * звуковой карты, и эти двое не договариваются. Кадр приходит то
 * раньше, то позже; иногда не приходит вовсе. Играй мы каждый кадр
 * «сразу по приходе» — получилась бы рваная речь с щелчками на каждом
 * стыке, потому что стык оказывался бы то внахлёст, то с дырой.
 *
 * ОТСЮДА БУФЕР. Первый кадр ставится в расписание не на «сейчас»,
 * а на «сейчас плюс сто пятьдесят миллисекунд», и дальше каждый
 * следующий встаёт на своё место ПО ВРЕМЕННОЙ МЕТКЕ, а не следом
 * за предыдущим. Разница видна на потерянном кадре: место под него
 * остаётся пустым, и поток не съезжает вперёд на его длину.
 *
 * ЕСЛИ ОПОЗДАЛИ — НАЧИНАЕМ ЗАНОВО. Кадр, чьё время уже прошло,
 * играть некуда: браузер сыграл бы его немедленно, поверх соседнего.
 * В такой момент расписание пересобирается от текущего времени —
 * слышно это как короткий провал, и это лучшее, что тут можно
 * сделать: догонять звуком нечего.
 *
 * WEBCODECS СПРЯТАН ЗА ИНТЕРФЕЙСОМ. Его нет ни в jsdom, где идут
 * тесты, ни в старых сборках CEF, где живёт оверлей. Оверлей без
 * него обязан остаться рабочим оверлеем — просто молчащим.
 */

import type { AirConfig, AirFrame } from '@owm/scene';

/** Задержка буфера: за столько кадр успевает доехать по TCP. */
const BUFFER_SEC = 0.15;

/** Раскодированный кусок звука. */
export interface AirAudio {
  /** Метка времени в микросекундах — та же, что уехала в декодер. */
  readonly timestampUs: number;
  readonly sampleRate: number;
  /** Отсчёты по каналам. */
  readonly channels: readonly Float32Array[];
}

/** То, что умеет декодер. */
export interface AirDecoder {
  decode(payload: Uint8Array, timestampUs: number): void;
  close(): void;
}

/**
 * Как создать декодер.
 *
 * Отдельной функцией, потому что это единственное место, где нужен
 * WebCodecs: подменив её, тест проверяет расписание кадров, не имея
 * ни кодека, ни звуковой карты.
 */
export type AirDecoderFactory = (
  config: AirConfig,
  onAudio: (audio: AirAudio) => void,
) => AirDecoder | null;

export interface AirPlayerOptions {
  readonly context: AudioContext;
  /** Куда включать поток. Обычно мастер графа. */
  readonly destination: AudioNode;
  readonly createDecoder?: AirDecoderFactory;
}

export interface AirPlayer {
  /** Эфир начался: настраиваем декодер. */
  start(config: AirConfig): void;
  /** Пришёл кадр. */
  push(frame: AirFrame): void;
  /** Эфир снят: глушим всё, что успели запланировать. */
  stop(): void;
}

export function createAirPlayer(options: AirPlayerOptions): AirPlayer {
  const { context, destination } = options;
  const createDecoder = options.createDecoder ?? webCodecsDecoder;

  let decoder: AirDecoder | null = null;
  let frameMs = 0;
  /** Опора расписания: метка кадра и время, на которое он поставлен. */
  let base: { us: number; at: number } | null = null;
  /** Уже запланированное: нужно, чтобы снятие эфира было мгновенным. */
  const playing = new Set<AudioBufferSourceNode>();

  const schedule = (audio: AirAudio): void => {
    const frames = audio.channels[0]?.length ?? 0;
    if (frames === 0) return;

    const now = context.currentTime;

    if (base === null) {
      base = { us: audio.timestampUs, at: now + BUFFER_SEC };
    }

    let at = base.at + (audio.timestampUs - base.us) / 1_000_000;

    if (at < now) {
      // Опоздали: расписание пересобирается от текущего времени.
      // Слышно это как короткий провал — но кадр, чьё время прошло,
      // браузер сыграл бы немедленно, поверх соседнего
      base = { us: audio.timestampUs, at: now + BUFFER_SEC };
      at = base.at;
    }

    const buffer = context.createBuffer(audio.channels.length, frames, audio.sampleRate);
    for (const [index, samples] of audio.channels.entries()) {
      // set в готовый канал, а не copyToChannel: второй требует
      // Float32Array именно над ArrayBuffer, а отсчёты приходят
      // от декодера, и над чем они — его дело
      buffer.getChannelData(index).set(samples);
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(destination);
    source.onended = () => playing.delete(source);
    source.start(at);

    playing.add(source);
  };

  const silence = (): void => {
    for (const source of playing) {
      // Уже отыгравший узел бросает на stop(): это не ошибка,
      // а гонка с его собственным окончанием
      try {
        source.stop();
      } catch {
        // отыграл сам
      }
    }
    playing.clear();
  };

  return {
    start(config: AirConfig): void {
      // Перезапуск, а не подстройка: сменились частота или число
      // каналов — прежний декодер отдавал бы отсчёты, которых мы
      // больше не ждём
      decoder?.close();
      base = null;
      frameMs = config.frameMs;
      decoder = createDecoder(config, schedule);
    },

    push(frame: AirFrame): void {
      if (decoder === null || frameMs === 0) return;

      // Метка считается из номера кадра, а не из часов: часы
      // отправителя и получателя не связаны ничем, а номер —
      // это ровно та сетка, по которой звук и записан
      decoder.decode(frame.payload, frame.seq * frameMs * 1000);
    },

    stop(): void {
      decoder?.close();
      decoder = null;
      base = null;
      silence();
    },
  };
}

/**
 * Декодер на WebCodecs.
 *
 * Возвращает null там, где WebCodecs нет: в старой сборке CEF
 * и в тестовом DOM. Оверлей от этого не ломается — он просто
 * не слышит эфир, и это лучше, чем упавшая отрисовка сцены.
 */
function webCodecsDecoder(
  config: AirConfig,
  onAudio: (audio: AirAudio) => void,
): AirDecoder | null {
  const Decoder = (globalThis as { AudioDecoder?: typeof AudioDecoder }).AudioDecoder;
  const Chunk = (globalThis as { EncodedAudioChunk?: typeof EncodedAudioChunk }).EncodedAudioChunk;

  if (Decoder === undefined || Chunk === undefined) return null;

  const decoder = new Decoder({
    output: (data: AudioData) => {
      const channels: Float32Array[] = [];

      for (let index = 0; index < data.numberOfChannels; index += 1) {
        const samples = new Float32Array(data.numberOfFrames);
        data.copyTo(samples, { planeIndex: index, format: 'f32-planar' });
        channels.push(samples);
      }

      onAudio({
        timestampUs: data.timestamp,
        sampleRate: data.sampleRate,
        channels,
      });

      data.close();
    },
    // Отказ декодера не роняет оверлей: поток мог прийти битым,
    // а сцена к нему отношения не имеет
    error: () => {},
  });

  decoder.configure({
    codec: 'opus',
    sampleRate: config.sampleRate,
    numberOfChannels: config.channels,
  });

  return {
    decode(payload: Uint8Array, timestampUs: number): void {
      // Все пакеты Opus — ключевые: межкадровой зависимости
      // у него нет, и потерянный кадр не портит следующие
      decoder.decode(new Chunk({ type: 'key', timestamp: timestampUs, data: payload }));
    },
    close(): void {
      // Закрытие уже закрытого декодера бросает: это гонка
      // с его собственной ошибкой, а не наша
      try {
        decoder.close();
      } catch {
        // уже закрыт
      }
    },
  };
}
